/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { API_DATE_FORMAT, CODE_TYPE_ADRESSE, DATE_FORMAT } from '@/constants';
import { type Mention, Sexe } from '@/types';

// ⚠️ Ce module est autonome : il fait son propre `dayjs.extend`.
// `useCreateActeNaissance` historique faisait `dayjs(x, 'DD/MM/YYYY')` SANS
// jamais appeler `dayjs.extend(customParseFormat)` lui-même : ça ne
// fonctionnait que par effet de bord de l'import de
// `validationInformationsNaissance.tsx` (qui l'appelle, lui). Un module
// autonome ne doit pas dépendre de cet ordre d'import fortuit, sous peine de
// parser silencieusement les dates de travers.
dayjs.extend(customParseFormat);

/**
 * Sexe au format lib → code INSEE du back (`ai_sexe`) : 1 masculin, 2 féminin,
 * 0 indéterminé — la convention de la base, que l'API complète par `I` /
 * `Indeterminé` dans `ai_sexec` / `ai_sexel`.
 *
 * ⚠️ Une valeur inconnue renvoie `undefined`, et non plus 2 : tout ce qui
 * n'était pas « homme » partait en féminin, « indéterminé » compris.
 */
const CODE_INSEE_BY_SEXE: Record<string, number> = {
  [Sexe.Homme]: 1,
  [Sexe.Femme]: 2,
  [Sexe.Indetermine]: 0,
};

export const formatSexe = (sexe: Sexe | string | undefined | null) =>
  _.isNil(sexe) || sexe === '' ? undefined : CODE_INSEE_BY_SEXE[sexe];

/**
 * Codification de `sexeCode`, renvoyé en lecture par la fiche et affiché tel
 * quel en « Civilité ». La mise à jour envoie `sexe` **et** `sexeCode` : le
 * mapping est isolé ici, c'est le seul endroit à corriger si l'API attend une
 * autre codification.
 */
export const SEXE_CODE_BY_SEXE: Record<string, string> = {
  homme: 'M',
  femme: 'F',
  indetermine: 'I',
};

/**
 * Libellé dénormalisé dans `ai_sexel`. Reproduit exactement les valeurs
 * présentes en base, « Indeterminé » et son orthographe incluse, pour que les
 * regroupements sur ce libellé restent homogènes.
 */
export const SEXE_LIBELLE_BY_SEXE: Record<string, string> = {
  homme: 'Masculin',
  femme: 'Féminin',
  indetermine: 'Indeterminé',
};

/** Date de naissance (3 champs de la lib) → 'YYYY-MM-DD' attendu par le back. */
export const formatDateNaissance = (individu: Record<string, any>) => {
  const { anneeNaissance, moisNaissance, jourNaissance } = individu ?? {};
  if (
    _.isNil(anneeNaissance) ||
    _.isNil(moisNaissance) ||
    _.isNil(jourNaissance)
  ) {
    return undefined;
  }
  return dayjs(
    `${anneeNaissance}-${moisNaissance}-${jourNaissance}`,
    'YYYY-M-D',
  ).format(API_DATE_FORMAT);
};

/**
 * Une date `DD/MM/YYYY` (saisie via `DateInput`) → `YYYY-MM-DD` attendu par le
 * back. Retourne `undefined` si absente ou invalide.
 */
export const formatDate = (date: string | undefined | null) => {
  if (_.isEmpty(date)) {
    return undefined;
  }
  const parsed = dayjs(date, DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format(API_DATE_FORMAT) : undefined;
};

/**
 * Lieu de naissance d'un individu (enfant ou parent), au format « à plat »
 * attendu par le back sous forme du quintuplet `naissance*`. Le même schéma
 * d'adresse (`adresseNaissance`) est utilisé 3 fois (enfant, parent1, parent2)
 * → extrait ici pour n'écrire ce mapping qu'une fois.
 */
export const formatLieuIndividu = (
  adresse: Record<string, any> | undefined,
) => ({
  naissanceDepartementCode: adresse?.codeDepartement,
  naissanceDepartementLibelle: adresse?.libelleDepartement,
  naissanceCodePostal: adresse?.libelleCodePostal,
  naissanceLocalite: adresse?.libelleCommune,
  naissancePaysLibelle: adresse?.libellePays,
});

/**
 * Lieu d'un individu sous un préfixe quelconque (`deces`, `domicile`), aux noms
 * sérialisés d'`ec_acte_individu`.
 *
 * ⚠️ Émet `${prefixe}Pays` et non `…PaysLibelle` : le back ne retraduit que la
 * naissance (`NumeriseActe::handleIndividu`).
 *
 * Le lieu de décès vit sur le DÉFUNT (`ai_dec_*`) bien qu'il soit saisi dans le
 * bloc « évènement » : `ad_dc_idlieu` n'est pas une FK de lieu.
 */
/** Suffixes des colonnes d'un lieu, sous le préfixe de `formatLieuIndividuPrefixe`. */
type SuffixeLieu =
  | 'DepartementCode'
  | 'DepartementLibelle'
  | 'CodePostal'
  | 'Localite'
  | 'Pays';

/**
 * Clés calculées (`${prefixe}…`) : sans type explicite, TypeScript les réduit à
 * `string` et l'objet ne garde aucune clé une fois étalé dans un payload.
 */
export const formatLieuIndividuPrefixe = <P extends string>(
  adresse: Record<string, any> | undefined,
  prefixe: P,
) =>
  ({
    [`${prefixe}DepartementCode`]: adresse?.codeDepartement,
    [`${prefixe}DepartementLibelle`]: adresse?.libelleDepartement,
    [`${prefixe}CodePostal`]: adresse?.libelleCodePostal,
    [`${prefixe}Localite`]: adresse?.libelleCommune,
    [`${prefixe}Pays`]: adresse?.libellePays,
  }) as Record<`${P}${SuffixeLieu}`, any>;

/**
 * Tronque une valeur à la largeur de sa colonne.
 *
 * ⚠️ Nécessaire, pas défensif : les colonnes d'adresse d'`ec_acte_individu` sont
 * étroites (`ai_dom_lib_voie` en `varchar(50)`, `ai_dom_de_code` en
 * `varchar(3)`) et PostgreSQL rejette le dépassement — HTTP 500 à
 * l'enregistrement.
 *
 * Le bornage est côté front parce que les tests d'API tournent sur SQLite, qui
 * ignore les largeurs de colonne : le dépassement y passerait inaperçu.
 */
const borner = (valeur: unknown, longueur: number) =>
  _.isString(valeur) ? valeur.slice(0, longueur) : valeur;

/**
 * Adresse COMPLÈTE (avec voie) d'un individu, sous un préfixe quelconque
 * (`domicile`), aux noms sérialisés d'`ec_acte_individu`.
 *
 * ⚠️ À distinguer de `formatLieuIndividuPrefixe`, qui n'émet que
 * commune/département/pays : un domicile est une adresse postale, voie incluse.
 *
 * Le `type` est converti en code entier (cf. `constants/typeAdresse`) :
 * `ai_dom_type` est numérique, y écrire « Commune » échouerait.
 *
 * `codeVoie` n'est pas émis — `AdresseForm` ne produit pas cette valeur.
 */
/** Suffixes des colonnes d'une adresse complète, cf. `formatAdressePrefixee`. */
type SuffixeAdresse =
  | 'Type'
  | 'NumeroVoirie'
  | 'ComplementNumeroVoirie'
  | 'LibelleVoie'
  | 'IdVoie'
  | 'ComplementDistribution'
  | 'ComplementIdentification'
  | 'ComplementGeographique'
  | 'CodePostal'
  | 'IdCodePostal'
  | 'Article'
  | 'Localite'
  | 'IdCommune'
  | 'DepartementCode'
  | 'DepartementLibelle'
  | 'IdDepartement'
  | 'Pays'
  | 'IdPays';

/** Partiel : les valeurs vides sont retirées (`_.omitBy`). */
type AdressePrefixee<P extends string> = Partial<
  Record<`${P}${SuffixeAdresse}`, any>
>;

export const formatAdressePrefixee = <P extends string>(
  adresse: Record<string, any> | undefined,
  prefixe: P,
): AdressePrefixee<P> => {
  if (_.isEmpty(adresse)) {
    return {};
  }

  const type = adresse?.type;

  return _.omitBy(
    {
      // Repli sur « Commune », le cas de loin le plus fréquent : sans clic sur
      // les boutons radio, l'adresse n'a pas de `type` et la colonne resterait
      // vide.
      [`${prefixe}Type`]: CODE_TYPE_ADRESSE[type ?? 'Commune'] ?? undefined,
      [`${prefixe}NumeroVoirie`]: borner(adresse?.numeroVoirie, 4),
      /*
       * ⚠️ L'indice de répétition (« Bis », « Ter »…) n'est PAS écrit.
       *
       * `ai_dom_lettre` est un `varchar(2)` qui contient de petits entiers, pas
       * un libellé, et aucun référentiel ne documente ce codage. Y écrire
       * « bis » lève un `SQLSTATE[22001]` ; y écrire un code deviné corromprait
       * la donnée en silence, ce qui est pire.
       *
       * Le champ reste saisissable mais non persisté, jusqu'à confirmation du
       * codage auprès du métier.
       */
      [`${prefixe}ComplementNumeroVoirie`]: borner(
        adresse?.complementNumeroVoirie,
        5,
      ),
      [`${prefixe}LibelleVoie`]: borner(adresse?.voie, 50),
      [`${prefixe}IdVoie`]: adresse?.identifiantVoie,
      [`${prefixe}ComplementDistribution`]: borner(
        adresse?.complementDistribution,
        50,
      ),
      [`${prefixe}ComplementIdentification`]: borner(
        adresse?.complementIdentification,
        50,
      ),
      [`${prefixe}ComplementGeographique`]: borner(
        adresse?.complementGeographique,
        50,
      ),
      [`${prefixe}CodePostal`]: borner(adresse?.codePostal, 10),
      [`${prefixe}IdCodePostal`]: adresse?.identifiantCodePostal,
      [`${prefixe}Article`]: borner(adresse?.article, 20),
      [`${prefixe}Localite`]: borner(adresse?.commune, 70),
      [`${prefixe}IdCommune`]: adresse?.identifiantCommune,
      [`${prefixe}DepartementCode`]: borner(adresse?.codeDepartement, 3),
      [`${prefixe}DepartementLibelle`]: borner(adresse?.departement, 50),
      [`${prefixe}IdDepartement`]: adresse?.identifiantDepartement,
      [`${prefixe}Pays`]: borner(adresse?.pays, 70),
      [`${prefixe}IdPays`]: adresse?.identifiantPays,
    },
    (valeur) => _.isNil(valeur) || valeur === '',
  ) as AdressePrefixee<P>;
};

/**
 * Idem `formatLieuIndividu`, mais aux noms du contrat **fiche** : la mise à
 * jour attend `naissancePays` là où la création attend `naissancePaysLibelle`.
 * Ce sont exactement les champs reçus en lecture, ce qui permet de fusionner le
 * retour dans l'individu affiché sans recharger la fiche.
 */
export const formatLieuIndividuFiche = (
  adresse: Record<string, any> | undefined,
) => ({
  naissanceDepartementCode: adresse?.codeDepartement,
  naissanceDepartementLibelle: adresse?.libelleDepartement,
  naissanceCodePostal: adresse?.libelleCodePostal,
  naissanceLocalite: adresse?.libelleCommune,
  naissancePays: adresse?.libellePays,
});

/**
 * Champs d'un individu tels que reçus en lecture dans la fiche. Le PUT
 * remplaçant le sous-objet individu, ils forment la base à renvoyer, que la
 * modal les édite ou non.
 */
export const CHAMPS_INDIVIDU_FICHE = [
  'identifiant',
  'sexe',
  'sexeCode',
  'nomFamille',
  'nomUsage',
  'prenom',
  'prenomsComplementaires',
  'naissanceDate',
  'naissanceHeure',
  'naissanceMinute',
  'naissanceDepartementCode',
  'naissanceDepartementLibelle',
  'naissanceCodePostal',
  'naissanceLocalite',
  'naissancePays',
  'profession',
  /*
   * Champs propres au DÉCÈS. Sans effet sur les autres types d'acte : `_.pick`
   * ignore une clé absente de l'individu lu.
   *
   * - `age` : le déclarant porte un âge, pas une date de naissance ;
   * - `deces*` : lieu du décès, porté par le DÉFUNT et non par l'acte ;
   * - `domicile*` : domicile du défunt et du déclarant.
   */
  'age',
  'decesDate',
  'decesHeure',
  'decesMinute',
  'decesDepartementCode',
  'decesDepartementLibelle',
  'decesCodePostal',
  'decesLocalite',
  'decesPays',
  // Domicile : adresse COMPLÈTE, d'où le jeu large de colonnes `ai_dom_*`.
  'domicileType',
  'domicileNumeroVoirie',
  'domicileLettre',
  'domicileComplementNumeroVoirie',
  'domicileLibelleVoie',
  'domicileIdVoie',
  'domicileComplementDistribution',
  'domicileComplementIdentification',
  'domicileComplementGeographique',
  'domicileCodePostal',
  'domicileIdCodePostal',
  'domicileArticle',
  'domicileLocalite',
  'domicileIdCommune',
  'domicileDepartementCode',
  'domicileDepartementLibelle',
  'domicileIdDepartement',
  'domicilePays',
  'domicileIdPays',
];

/**
 * Lieu de reconnaissance (`acte.lieuReconnaissance`), au format imbriqué du
 * contrat de l'endpoint `POST /numerisation-actes/reconnaissance`.
 *
 * ⚠️ Noms de champs différents de `formatLieuIndividu` (`codePostal` et non
 * `naissanceCodePostal`, etc.) : ce n'est pas un individu mais un lieu
 * autonome, imbriqué sous `acte.lieuReconnaissance`, pas aplati.
 *
 * `NaissanceForm` ne produit ni `complementDistribution` ni `codeInsee` — on
 * envoie ce qu'on a (les `identifiant*` de la sélection assistée), cohérent
 * avec `aa_cpl_distrib` qui n'est jamais renseigné en base.
 */
export const formatLieuActe = (adresse: Record<string, any> | undefined) => {
  if (
    _.isEmpty(adresse?.libelleCommune) &&
    _.isEmpty(adresse?.codeDepartement)
  ) {
    return undefined;
  }
  return {
    codeDepartement: adresse?.codeDepartement,
    libelleDepartement: adresse?.libelleDepartement,
    codePostal: adresse?.libelleCodePostal,
    libelleCommune: adresse?.libelleCommune,
    libellePays: adresse?.libellePays,
    identifiantCommune: adresse?.identifiantCommune,
    identifiantDepartement: adresse?.identifiantDepartement,
    identifiantPays: adresse?.identifiantPays,
    codeInsee: adresse?.codeInsee,
  };
};

/**
 * Sens donné à une filiation non renseignée, selon le type d'acte.
 *
 * - `decede` (naissance) : le parent est décédé → `ai_vivant = 'N'`.
 * - `inconnu` (reconnaissance) : aucun drapeau « inconnu » n'existe dans les
 *   tables `ec_*`. La convention, relevée sur le fonds repris, est un individu
 *   créé **sans nom** avec `ai_vivant` laissé **vide**.
 */
export type SensFiliationNonRenseignee = 'decede' | 'inconnu';

/**
 * Profession au format attendu par le back (simple chaîne, colonne
 * `ec_acte_individu.ai_profession`).
 *
 * Deux formes coexistent en entrée : le champ « Profession » de l'assistant
 * produit une **chaîne**, alors que certains écrans de la lib stockent un
 * **objet `{ libelle }`**. Les deux sont acceptées ici — ne lire que
 * `profession?.libelle` ferait silencieusement perdre la saisie de l'assistant.
 */
export const formatProfession = (profession: unknown): string | undefined => {
  const libelle = _.isPlainObject(profession)
    ? (profession as Record<string, any>).libelle
    : profession;

  if (!_.isString(libelle) || _.isEmpty(libelle.trim())) {
    return undefined;
  }

  return libelle.trim();
};

/**
 * Parent au format back ; absent du payload s'il est vide.
 *
 * Les deux sens d'une filiation non renseignée n'ont PAS le même effet sur les
 * données transmises :
 *
 * - `inconnu` (reconnaissance) : le parent n'a par convention aucune donnée. La
 *   lib grise ses champs, le back crée un individu sans nom. Seul le drapeau
 *   part.
 * - `decede` (naissance) : le parent **garde son état civil**. « Décédé » est
 *   une information complémentaire qui positionne `ai_vivant = 'N'`, pas une
 *   absence de filiation — la lib laisse d'ailleurs ses champs saisissables
 *   (`ParentForm`, `disabledForFiliationInconnue`).
 *
 * ⚠️ Ne renvoyer que `{ decede: true }` perdait toute la saisie du parent :
 * nom, prénoms, lieu de naissance et profession étaient silencieusement jetés.
 */
/**
 * Bloc d'un parent dans les payloads de création : un seul type pour les trois
 * formes (`{ inconnu }`, état civil, état civil `decede`), dont toutes les clés
 * sont donc optionnelles.
 */
export type ParentPayload = Partial<ReturnType<typeof formatLieuIndividu>> & {
  inconnu?: boolean;
  decede?: boolean;
  nomFamille?: string;
  prenom?: string;
  prenomsComplementaires?: string;
  naissanceDate?: string;
  profession?: string;
  sexe?: number;
};

export const formatParent = (
  parent: Record<string, any> | undefined,
  filiationNonRenseignee = false,
  sens: SensFiliationNonRenseignee = 'decede',
): ParentPayload | undefined => {
  if (filiationNonRenseignee && sens === 'inconnu') {
    return { inconnu: true };
  }

  const etatCivil =
    _.isEmpty(parent) || _.isEmpty(parent?.nomFamille)
      ? undefined
      : {
          nomFamille: parent.nomFamille,
          prenom: parent.prenom,
          prenomsComplementaires: parent.prenomsComplementaires,
          naissanceDate: formatDateNaissance(parent),
          ...formatLieuIndividu(parent.adresseNaissance),
          profession: formatProfession(parent.profession),
          ...(!_.isNil(formatSexe(parent.sexe)) && {
            sexe: formatSexe(parent.sexe),
          }),
        };

  // `{ ...undefined }` vaut `{}` : un parent décédé dont aucun nom n'a été
  // saisi retombe donc sur le seul drapeau, comme avant.
  if (filiationNonRenseignee) {
    return { ...etatCivil, decede: true };
  }

  return etatCivil && { ...etatCivil, decede: false };
};

/**
 * Coordonnées du registre auquel l'acte est rattaché, communes aux trois types
 * d'acte numérisés.
 *
 * ⚠️ Les noms du contrat sont trompeurs, à lire une fois pour toutes :
 *
 * | Champ envoyé     | Colonne      | Ce que c'est vraiment     |
 * |------------------|--------------|---------------------------|
 * | `anneeRegistre`  | `*_rg_num`   | le **numéro** du registre |
 * | `typeRegistre`   | `*_rg_typ`   | le type du registre       |
 * | `anneeActe`      | `*_rg_annee` | l'**année du registre**   |
 * | `dateDresseActe` | `*_a_date`   | la date de l'acte         |
 *
 * ⚠️ `anneeActe` doit venir du registre CHOISI, pas de la date de l'acte : le
 * triplet (`*_rg_num`, `*_rg_typ`, `*_rg_annee`) est la clé composite vers
 * `ec_registre`. Un acte dressé en 2026 sur le registre 2025 produisait une
 * référence pendante — sans erreur, et invisible sur la fiche (qui lit les
 * colonnes dénormalisées de l'acte) mais rendant la colonne « Registre » vide
 * dans la liste, qui joint réellement `ec_registre`.
 *
 * Le repli sur l'année de l'acte ne sert que si l'arbre des registres n'a pas
 * chargé sa relation `annee`.
 */
export const formatCoordonneesRegistre = (
  selectedRegistre: Record<string, any> | undefined,
  dateActe: string | undefined | null,
) => ({
  libelleRegistre: selectedRegistre?.libelle,
  typeRegistre: selectedRegistre?.type,
  anneeRegistre: selectedRegistre?.numero,
  ...(!_.isNil(dateActe) && { dateDresseActe: formatDate(dateActe) }),
  ...(() => {
    const anneeRegistre = selectedRegistre?.annee?.annee;

    if (!_.isNil(anneeRegistre)) {
      return { anneeActe: Number(anneeRegistre) };
    }

    return _.isNil(dateActe) ? {} : { anneeActe: formatAnneeActe(dateActe) };
  })(),
});

/** Année de la date de l'acte (`dateActe`, `'DD/MM/YYYY'`) → entier attendu par le back. */
export const formatAnneeActe = (dateActe: string | undefined | null) => {
  if (_.isNil(dateActe)) {
    return undefined;
  }
  return dayjs(dateActe, DATE_FORMAT).year();
};

/** Mentions de l'étape 3, au format back (`mentions[]`). */
export const formatMentions = (mentions: Mention[] = []) =>
  _.map(mentions, (mention) => ({
    titre: mention.titre,
    texte: mention.texte,
    ...(mention.numero && { numero: Number(mention.numero) }),
    ...(mention.dateApposition && {
      dateApposition: formatDate(mention.dateApposition),
    }),
    // Code de catégorie ANTS, tel que l'ETCV le stocke.
    ...(mention.codeAnts !== '' && { codeMentionAnts: mention.codeAnts }),
  }));
